import { useEffect, useRef, useState } from 'react';
import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import type { Prospect, ProspectPoint } from '@prototypical/types';

let configured = false;
export default function ProspectMap({ apiKey, center, radius, prospects, selected, discover, onSelect, onCenter }: {
  apiKey: string | null; center: ProspectPoint | null; radius: number; prospects: Prospect[]; discover: boolean;
  selected: string | null; onSelect: (id: string) => void; onCenter: (point: ProspectPoint) => void;
}) {
  const node = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [error, setError] = useState('');
  const [fit, setFit] = useState(0);
  const callbacks = useRef({ onSelect, onCenter, discover });
  callbacks.current = { onSelect, onCenter, discover };
  useEffect(() => {
    if (!apiKey) return;
    let disposed = false;
    let instance: google.maps.Map | null = null;
    if (!configured) { setOptions({ key: apiKey, v: 'quarterly', authReferrerPolicy: 'origin' }); configured = true; }
    void importLibrary('maps').then(({ Map }) => {
      if (disposed || !node.current) return;
      instance = new Map(node.current, { center: { lat: 39.5, lng: -98.35 }, zoom: 4, mapId: 'DEMO_MAP_ID', mapTypeControl: false, streetViewControl: false });
      instance.addListener('click', (event: google.maps.MapMouseEvent) => { if (event.latLng && callbacks.current.discover) callbacks.current.onCenter(event.latLng.toJSON()); });
      setMap(instance);
    }).catch(() => { if (!disposed) setError('The map could not load. You can still search and select businesses from the results.'); });
    return () => { disposed = true; if (instance) google.maps.event.clearInstanceListeners(instance); };
  }, [apiKey]);
  useEffect(() => {
    if (!map || !center || !discover) return;
    const circle = new google.maps.Circle({ map, center, radius, fillColor: '#078ac4', fillOpacity: 0.07, strokeColor: '#078ac4', strokeWeight: 2, clickable: false });
    const bounds = circle.getBounds(); if (bounds) map.fitBounds(bounds, 28);
    return () => circle.setMap(null);
  }, [map, center, radius, discover]);
  useEffect(() => {
    if (!map || (discover && fit === 0)) return;
    const locations = prospects.flatMap(prospect => prospect.location ? [prospect.location] : []);
    if (!locations.length) return;
    if (locations.length === 1) { map.setCenter(locations[0]); map.setZoom(14); return; }
    const bounds = new google.maps.LatLngBounds(); locations.forEach(location => bounds.extend(location)); map.fitBounds(bounds, 48);
  }, [map, prospects, discover, fit]);
  useEffect(() => {
    if (!map) return;
    let disposed = false;
    const markers: google.maps.marker.AdvancedMarkerElement[] = [];
    void importLibrary('marker').then(({ AdvancedMarkerElement, PinElement }) => {
      if (disposed) return;
      for (const prospect of prospects) {
        if (!prospect.location) continue;
        const pin = new PinElement({ background: selected === prospect.placeId ? '#172033' : '#078ac4', borderColor: '#fff', glyphColor: '#fff' });
        const marker = new AdvancedMarkerElement({ map, position: prospect.location, title: prospect.name, content: pin.element });
        marker.addListener('click', () => callbacks.current.onSelect(prospect.placeId));
        markers.push(marker);
      }
    }).catch(() => { if (!disposed) setError('Map pins could not load. Select a business from the results.'); });
    return () => { disposed = true; markers.forEach(marker => { google.maps.event.clearInstanceListeners(marker); marker.map = null; }); };
  }, [map, prospects, selected]);
  const prospectsRef = useRef(prospects); prospectsRef.current = prospects;
  useEffect(() => {
    const location = prospectsRef.current.find(prospect => prospect.placeId === selected)?.location;
    if (map && location) map.panTo(location);
  }, [map, selected]);
  return <div className="prospect-map-wrap">
    <div className="prospect-map" ref={node} aria-label={discover ? 'Business discovery map' : 'Saved businesses map'} />
    {(!apiKey || error) && <p className="prospect-map-message" role="status">{error || 'The map is not configured yet. The saved business list, details and downloads remain available.'}</p>}
    {apiKey && !error && !discover && prospects.length > 0 && !prospects.some(prospect => prospect.location) && <p className="prospect-map-message" role="status">These saved businesses have no coordinates yet. Use Resolve all missing locations above to put them on the map.</p>}
    {apiKey && !error && <div className="prospect-map-controls"><p>{discover ? 'Click the map to choose a search area.' : `${prospects.filter(prospect => prospect.location).length} mapped businesses match your filters.`}</p><button className="btn" disabled={!map || !prospects.some(prospect => prospect.location)} onClick={() => setFit(value => value + 1)}>Fit all pins</button></div>}
  </div>;
}
